import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "src/environments/environment";
import {
  Map,
  NavigationControl,
  MapboxOptions,
  GeolocateControl,
  Marker,
  MarkerOptions,
  Popup,
  PopupOptions,
  LngLatLike,
  LngLatBounds,
} from "mapbox-gl";
import { of } from "rxjs";
import { tap } from "rxjs/operators";

interface CustomMapboxOptions extends MapboxOptions {
  accessToken?: string;
}

interface CustomMarkerOptions extends MarkerOptions {
  lngLat: [number, number];
}

interface CustomPopupOptions extends PopupOptions {
  text?: string;
  html?: string;
}

@Injectable({
  providedIn: "root",
})
export class MapBoxService {
  accessToken = environment.mapBoxToken;
  basePath = environment.mapBoxUrl;
  map: Map;
  markers: Marker[] = [];

  constructor(private http: HttpClient) {}

  cleanMap() {
    if (this.map) {
      this.map.remove();
    }
  }

  buildMap(options: CustomMapboxOptions) {
    const fullOptions: MapboxOptions = {
      ...options,
      accessToken: this.accessToken,
    };
    this.map = new Map(fullOptions);
    this.map.addControl(new NavigationControl());
    this.map.addControl(
      new GeolocateControl({
        showUserLocation: true,
      }),
    );
  }

  addMarker(options: CustomMarkerOptions) {
    const marker = new Marker(options);
    const coordinates: LngLatLike = {
      lng: options.lngLat[0],
      lat: options.lngLat[1],
    };
    marker.setLngLat(coordinates);
    marker.addTo(this.map);
    this.markers.push(marker);
    if (this.map.getCenter().lat === 0 && this.map.getCenter().lng === 0) {
      this.map.setCenter(coordinates);
    }
    return marker;
  }

  createPopup(options: CustomPopupOptions) {
    const popup = new Popup({
      ...options,
      closeButton: false,
      closeOnClick: true,
      closeOnMove: true,
    });
    if (options?.text) {
      popup.setText(options.text);
    }
    if (options?.html) {
      popup.setHTML(options.html);
    }
    return popup;
  }

  fitBounds() {
    const bounds = new LngLatBounds();
    this.markers.forEach((feature) => {
      bounds.extend(feature.getLngLat());
    });
    this.map.fitBounds(bounds, { maxZoom: 10, padding: 50 });
  }

  getAddress(options: { [key: string]: any }) {
    const params: HttpParams = new HttpParams().set("access_token", this.accessToken);

    const searchText = encodeURIComponent(Object.values(options).join(" "));

    const locationCordinates = JSON.parse(localStorage.getItem(searchText));
    if (locationCordinates) {
      return of(locationCordinates); // returning from cache
    }

    return this.http.get<any>(`${this.basePath}/${searchText}.json`, { params }).pipe(
      tap((response: any) => {
        localStorage.setItem(searchText, JSON.stringify(response));
      }),
    );
  }
}
